import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';
import { getCancellationPolicyText } from 'utils/getCancellationPolicyText';
import { MappedBookingObject } from 'components/payment/receipt/components/swipe-receipt-content.interface';

/**
 * The booking's cancellation policy, as the sentence a tenant reads -- or an empty string where the
 * booking has no policy worth printing.
 *
 * RES-1367: lifted out of `ReceiptSummary`, which used to be the only place that rendered this
 * section. The booking-details card now renders it itself (below Amenities, where the design puts
 * it) and the summary is told to leave it out -- so two components need the same answer, and a
 * twelve-entry dependency list is not a thing to keep two copies of. One definition, so the card
 * and the receipt can never quote a different policy for the same booking.
 */
export const useCancellationPolicyText = (transaction?: MappedBookingObject): string => {
  const intl = useIntl();
  const locale = useLocale();
  const details = transaction?.details;

  return useMemo(
    () =>
      transaction
        ? getCancellationPolicyText(
            {
              allowCancelDuring: details?.allow_cancel_during,
              allowLateCancel: details?.allow_late_cancel,
              cancelDuringFeeAmount: details?.cancel_during_fee_amount,
              cancelDuringFeePercent: details?.cancel_during_fee_percent,
              lateCancelFeeAmount: details?.late_cancel_fee_amount,
              lateCancelFeePercent: details?.late_cancel_fee_percent,
              cancellationWindowEndMinutes: details?.cancellation_window_end_minutes,
              cancellationDescription: details?.cancellation_description,
              currencyType: transaction.currency_type,
              locale: locale as string,
            },
            intl,
            transaction.total_price > 0,
          )
        : '',
    [
      transaction,
      details?.allow_cancel_during,
      details?.allow_late_cancel,
      details?.cancel_during_fee_amount,
      details?.cancel_during_fee_percent,
      details?.late_cancel_fee_amount,
      details?.late_cancel_fee_percent,
      details?.cancellation_window_end_minutes,
      details?.cancellation_description,
      intl,
      locale,
    ],
  );
};
